import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { reportError } from "../lib/errorReporter";

/**
 * Fetch every dashboard dataset in parallel, respecting:
 *   * A single ``AbortController`` per (orgId, from, to, owner, team) tuple —
 *     any new tuple or an unmount cancels the previous requests, so a slow
 *     response for the previous scope can NEVER overwrite the current data.
 *   * A monotonic request-id guard as a belt-and-suspenders check in case the
 *     underlying HTTP client silently ignores an abort.
 *   * A ``background`` flag for polled refreshes so we do not flicker back to
 *     the loading skeleton on every 60-second tick.
 *
 * Phase 2D.3B.3 — the two classes of analytics are requested differently:
 *   * PERIOD:        summary, funnel, accepted-trend, by-owner (from/to);
 *   * CURRENT STATE: status-breakdown, by-team, workload, aging — no period at
 *     all, because "what exists right now" must never be hidden by a date range.
 *
 * Returned:
 *   { summary, funnel, trend, aging, byOwner, statuses, byTeam, workload,
 *     loading, refreshing, error, lastUpdatedAt, refresh }
 *
 * ``refresh({ background })`` re-fetches the same tuple. It is stable across
 * renders so it is safe to pass to ``useVisibleInterval``.
 */
export function useDashboardData({
  orgId,
  from,
  to,
  owner,
  team,
  includeTeamReports = true,
  translateError,
}) {
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [trend, setTrend] = useState(null);
  const [aging, setAging] = useState(null);
  const [byOwner, setByOwner] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [byTeam, setByTeam] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  // Track the most recent tuple so stale callbacks can compare & bail out.
  const activeKeyRef = useRef(null);
  const reqIdRef = useRef(0);
  const abortRef = useRef(null);
  // True while a batch is on the wire. A PERIODIC tick is skipped while it is
  // set, so polling can never stack requests on a slow connection. A scope
  // change is NOT skipped: that data is stale by definition, so it cancels the
  // in-flight batch and refetches.
  const inFlightRef = useRef(false);

  // Latest translator kept in a ref so the fetch identity does not depend on
  // an i18n function whose identity may change across renders.
  const translateRef = useRef(translateError);
  translateRef.current = translateError;

  const key =
    orgId && from && to
      ? `${orgId}|${from}|${to}|${owner || ""}|${team || ""}|${includeTeamReports ? 1 : 0}`
      : null;

  const doFetch = useCallback(
    async ({ background = false } = {}) => {
      if (!key) return;
      if (background && inFlightRef.current) return;

      // Abort any in-flight requests for the previous or same key.
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      const rid = ++reqIdRef.current;
      const activeKey = key;
      activeKeyRef.current = activeKey;

      inFlightRef.current = true;
      if (background) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const scopeParams = {};
      if (owner) scopeParams.owner_id = owner;
      if (team) scopeParams.team_id = team;

      const cfgPeriod = {
        params: { from, to, ...scopeParams },
        signal: controller.signal,
      };
      // Current-state reads deliberately carry NO period.
      const cfgCurrent = { params: { ...scopeParams }, signal: controller.signal };
      // by-owner compares people inside the scope; the person filter would
      // collapse it to a single row, so only the team travels with it.
      const cfgByOwner = {
        params: { from, to, ...(team ? { team_id: team } : {}) },
        signal: controller.signal,
      };
      const cfgByTeam = {
        params: { ...(team ? { team_id: team } : {}) },
        signal: controller.signal,
      };

      try {
        const [s, f, tr, ag, st, bo, bt, wl] = await Promise.all([
          api.get("/dashboard/summary", cfgPeriod),
          api.get("/dashboard/funnel", cfgPeriod),
          api.get("/dashboard/accepted-trend", cfgPeriod),
          api.get("/dashboard/aging", cfgCurrent),
          api.get("/dashboard/status-breakdown", cfgCurrent),
          api.get("/dashboard/by-owner", cfgByOwner),
          includeTeamReports
            ? api.get("/dashboard/by-team", cfgByTeam)
            : Promise.resolve({ data: null }),
          includeTeamReports
            ? api.get("/dashboard/workload", cfgCurrent)
            : Promise.resolve({ data: null }),
        ]);
        if (rid !== reqIdRef.current || activeKeyRef.current !== activeKey) {
          return; // stale
        }
        setSummary(s.data);
        setFunnel(f.data);
        setTrend(tr.data);
        setAging(ag.data);
        setStatuses(st.data);
        setByOwner(bo.data);
        setByTeam(bt.data);
        setWorkload(wl.data);
        setLastUpdatedAt(new Date());
      } catch (err) {
        // Silent for aborted requests — that is a controlled cancel, not a bug.
        const isAbort =
          err?.name === "CanceledError" ||
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED";
        if (isAbort) return;
        if (rid !== reqIdRef.current || activeKeyRef.current !== activeKey) return;
        reportError("dashboard.load", err);
        setError(translateRef.current ? translateRef.current(err) : "error");
      } finally {
        if (rid === reqIdRef.current && activeKeyRef.current === activeKey) {
          inFlightRef.current = false;
          if (background) setRefreshing(false);
          else setLoading(false);
        }
      }
    },
    [key, from, to, owner, team, includeTeamReports],
  );

  // Fetch fresh data whenever the tuple changes. This effect also cancels the
  // previous fetch (via ``doFetch``) so display never lags behind selection.
  useEffect(() => {
    if (!key) return undefined;
    doFetch({ background: false });
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [key, doFetch]);

  const refresh = useCallback(
    (opts) => doFetch(opts),
    [doFetch],
  );

  return {
    summary,
    funnel,
    trend,
    aging,
    byOwner,
    statuses,
    byTeam,
    workload,
    loading,
    refreshing,
    error,
    lastUpdatedAt,
    refresh,
  };
}
